import { Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';

import { ifAsset } from 'stores/DynamicProperties/casts';
import { maybeAddChangeListener } from 'stores/DynamicProperties/changeListeners';
import { type IDynamicProperty } from 'stores/DynamicProperties/IDynamicProperty';
import { getOwningCreative } from 'stores/DynamicProperties/tree';
import { AssetFileType } from 'types/AssetFileType';
import { useForceUpdate } from 'utils/Hooks';

import { ScalarContainer } from './ScalarContainer';

const { Option } = Select;

export interface AssetInputProps {
  property: IDynamicProperty<any>;
  disabled?: boolean;
}

function AssetInput({
  property: uncheckedProperty,
  disabled,
}: AssetInputProps) {
  const forceUpdate = useForceUpdate();

  const property = ifAsset(uncheckedProperty);
  const assetsManager = property && getOwningCreative(property)?.assets;

  // `observer` covers assetsManager.children, which is a MobX observable array.
  // It does nothing for `property`, which is a DynamicProperty and never calls
  // becomeObservable - so the value shown here only refreshes because this
  // event handler forces it to.
  useEffect(
    () => maybeAddChangeListener(property, forceUpdate),
    [property, forceUpdate]
  );

  const handleChange = (value: string) => property?.set(value);

  const options = [
    <Option key={'(none)'} value={''}>
      {'Select None'}
    </Option>,

    ...(assetsManager?.children ?? [])
      .filter((item) => item.data.type !== AssetFileType.FOLDER)
      .map((item) => (
        <Option key={item.id} value={item.id}>
          {item.data.path}
        </Option>
      )),
  ];

  const currentId = property && assetsManager?.getById(property.value)?.id;

  return (
    <ScalarContainer size="full">
      <Select
        showSearch
        style={{ width: '100%' }}
        value={currentId}
        onChange={handleChange}
        placeholder="Select an asset"
        disabled={disabled || !property}
        // Required, not decorative: each option's `value` is an asset id, and
        // the default search matches on `value`. Without this, typing would
        // search Firestore ids rather than the paths on screen.
        optionFilterProp="children"
      >
        {options}
      </Select>
    </ScalarContainer>
  );
}

export default observer(AssetInput);
